import React, { useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get } from 'underscore';
import classNames from 'classnames';
import { DefaultButton } from '@bingads-webui-react/fabric-control';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withPreference } from '@bingads-webui-react/preference-context';
import {
  getUrlPrefixAndDestination,
  getUpdatedFullUrlOnPrefixUpdate,
  getUpdatedFullUrlOnTextUpdate,
  validateAdUrl
} from '@bingads-webui-campaign-react/url-input-fluent';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useWebsiteSuggestion } from '@bingads-webui-campaign-react/add-image-hooks';

import { AddImage } from './add-image';

const getStyles = () => ({
  buttonContainer: {
    display: 'flex',
    padding: '32px',
    height: '134px',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '12px',
    marginTop: '16px',
    border: '1px dashed #B3B0AD',
  },
});

const PopulateImages = ({
  aid,
  i18n,
  perfMarker,
  preferenceService,
  permissions,
  oDataConfig,
  serverContext,
  onNewImageSelected,
  selectedMediaPanelSource,
  setSelectedMediaPanelSource,
  displayImagePicker,
  setDisplayImagePicker,
  instrumentation,
  campaignId,
  onUpdateUrl,
  fullUrl,
  maxUploadNum,
  showAddButton,
  creativeEditorProps,
}) => {
  const classes = useDefaultStyles(getStyles);
  const activity = useContext(ActivityContext);
  const scenario = useContext(ScenarioContext);
  const [privateUrl, setPrivateUrl] = useState('');

  const url = useMemo(() => {
    if (validateAdUrl(fullUrl)=== null) {
      return fullUrl;
    }

    return privateUrl || 'https://';
  }, [fullUrl, privateUrl]);

  const urlPrefixValue = useMemo(() => {
    const [prefix] = getUrlPrefixAndDestination(url);
    return prefix;
  }, [url]);

  const {
    suggestionState,
    suggestionPoller,
  } = useWebsiteSuggestion({
    oDataConfig,
    adContext: {
      advertiserCustomerId: get(serverContext, ['CurrentAccount', 'AdvertiserCustomerID']),
      accountId: aid,
      campaignId,
    },
    instrumentation,
    logActivity: activity.signal,
    permissions,
    fullUrl: url,
    hasErrorInUrl: validateAdUrl(url) !== null,
    adCreatorScenario: {
      scenarioName: `${scenario.name}AdCreation`,
      scenarioValue: `${scenario.name}AdCreation`,
    },
  });

  const updateUrl = (evt) => {
    const { value } = evt.target;
    const newUrl = getUpdatedFullUrlOnTextUpdate(urlPrefixValue, value);
    if (onUpdateUrl) {
      onUpdateUrl(newUrl);
    } else {
      setPrivateUrl(newUrl);
    }
  };

  const updateUrlPrefix = (evt, { text }) => {
    const newPrefix = text.substring(0, text.length - 3);
    const [, original] = getUrlPrefixAndDestination(url);
    const newUrl = getUpdatedFullUrlOnPrefixUpdate(original, newPrefix);
    if (onUpdateUrl) {
      onUpdateUrl(newUrl);
    } else {
      setPrivateUrl(newUrl);
    }
  };

  return (
    <>
      { showAddButton &&
        <div className={classNames(classes.buttonContainer, 'add-image-button-container')}>
          <DefaultButton
            text={i18n.getString(_TL_('Add images'))}
            onClick={() => {
              setDisplayImagePicker(true);
            }}
            className="add-image-button"
            styles={{ root: { minWidth: '200px', marginTop: 16 } }}
          />
        </div>
      }
      <AddImage
        displayImagePicker={displayImagePicker}
        setDisplayImagePicker={setDisplayImagePicker}
        selectedMediaPanelSource={selectedMediaPanelSource}
        setSelectedMediaPanelSource={setSelectedMediaPanelSource}
        onNewImageSelected={onNewImageSelected}
        advertiserCustomerId={get(serverContext, ['CurrentAccount', 'AdvertiserCustomerID'])}
        accountId={aid}
        i18n={i18n}
        perfMarker={perfMarker}
        preferenceService={preferenceService}
        permissions={permissions}
        oDataConfig={oDataConfig}
        suggestionState={suggestionState}
        suggestionPoller={suggestionPoller}
        fullUrl={url}
        urlPrefix={urlPrefixValue}
        onUpdateUrlPrefix={updateUrlPrefix}
        onUpdateUrlValue={updateUrl}
        dateFormat={get(serverContext, ['CurrentUser', 'CultureDateTimeFormat', 'ShortDatePattern'])}
        timeZone={get(serverContext, ['CurrentAccount', 'TimeZone'])}
        activityLogger={activity}
        maxUploadNum={maxUploadNum}
        isAADUser={serverContext && serverContext.IsAADUser}
        creativeEditorProps={creativeEditorProps}
        serverContext={serverContext}
      />
    </>);
};

PopulateImages.propTypes = {
  aid: PropTypes.number.isRequired,
  campaignId: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  perfMarker: PropTypes.shape({
  }).isRequired,
  preferenceService: PropTypes.shape({
    setAtUserLevel: PropTypes.func,
    findByNameAtUserLevel: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
    }),
  }).isRequired,
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      AdvertiserCustomerID: PropTypes.number,
      TimeZone: PropTypes.number,
    }),
    CurrentUser: PropTypes.shape({
      CultureDateTimeFormat: PropTypes.shape({
        ShortDatePattern: PropTypes.string,
      }),
    }),
  }).isRequired,
  onNewImageSelected: PropTypes.func.isRequired,
  selectedMediaPanelSource: PropTypes.string.isRequired,
  setSelectedMediaPanelSource: PropTypes.func.isRequired,
  displayImagePicker: PropTypes.bool.isRequired,
  setDisplayImagePicker: PropTypes.func.isRequired,
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }).isRequired,
  onUpdateUrl: PropTypes.func,
  fullUrl: PropTypes.string,
  maxUploadNum: PropTypes.number,
  showAddButton: PropTypes.bool,
  creativeEditorProps: PropTypes.shape({}),
};

PopulateImages.defaultProps = {
  onUpdateUrl: undefined,
  fullUrl: '',
  maxUploadNum: 10,
  showAddButton: true,
  creativeEditorProps: undefined,
};

const wrapped = withPreference(withInstrumentation(PopulateImages));

export { wrapped as PopulateImages };
